import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getHotelDateFormat } from '../../utils/dateUtils';
import { fetchDraftInvoiceDefaults } from '../../services/api';
import { getBookingDrawerTheme } from '../../utils/bookingDrawerLayout';

const emptyLineItem = () => ({
  description: '',
  room_type: 'Room',
  rate_plan: '',
  room_count: 1,
  rate_per_night: 0,
  amount: 0,
  auto_calculate: true,
});

const DISPLAY_DATE_FORMATS = ['DD MMM YYYY', 'D MMM YYYY', 'YYYY-MM-DD'];

function parseDisplayDate(value) {
  if (!value) return null;
  const parsed = moment(value, DISPLAY_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function formatDisplayDate(value) {
  if (!value) return '';
  const parsed = moment.isMoment(value) ? value : parseDisplayDate(value);
  return parsed?.isValid() ? parsed.format('DD MMM YYYY') : '';
}

const TIME_PARSE_FORMATS = ['HH:mm', 'HH:mm:ss', 'H:mm', 'h:mm A', 'hh:mm A'];

function parseDisplayTime(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const parsed = moment(String(value).trim(), TIME_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function formatTimeForApi(value) {
  const parsed = parseDisplayTime(value);
  return parsed?.isValid() ? parsed.format('HH:mm') : '';
}

function computeNightsFromDates(checkIn, checkOut) {
  const start = parseDisplayDate(checkIn);
  const end = parseDisplayDate(checkOut);
  if (!start?.isValid() || !end?.isValid()) return null;
  return Math.max(0, end.diff(start, 'days'));
}

function effectiveNights(nights) {
  return Math.max(1, parseInt(nights, 10) || 1);
}

function effectiveRooms(roomCount) {
  return Math.max(1, parseInt(roomCount, 10) || 1);
}

function computeRatePerNight(amount, roomCount, nights) {
  const n = effectiveNights(nights);
  const rooms = effectiveRooms(roomCount);
  const total = parseNum(amount);
  if (!total) return 0;
  return Math.round((total / n / rooms) * 100) / 100;
}

function computeLineAmount(ratePerNight, roomCount, nights) {
  const n = effectiveNights(nights);
  const rooms = effectiveRooms(roomCount);
  return Math.round(parseNum(ratePerNight) * n * rooms * 100) / 100;
}

function normalizeLineItem(row, nights) {
  const n = effectiveNights(nights);
  const autoCalculate = row.auto_calculate !== false;
  const rate = row.rate_per_night != null && row.rate_per_night !== ''
    ? parseNum(row.rate_per_night)
    : computeRatePerNight(row.amount, row.room_count, n);
  return {
    ...row,
    auto_calculate: autoCalculate,
    rate_per_night: rate,
    amount: autoCalculate ? computeLineAmount(rate, row.room_count, n) : parseNum(row.amount),
  };
}

function recalculateLineItemsForNights(lineItems, nights) {
  return (lineItems || []).map((row) => normalizeLineItem(row, nights));
}

function applyStayDatePatch(draft, patch) {
  const next = { ...draft, ...patch };
  if ('check_in' in patch || 'check_out' in patch) {
    const nights = computeNightsFromDates(next.check_in, next.check_out);
    if (nights != null) next.nights = nights;
  }
  if ('check_in' in patch || 'check_out' in patch || 'nights' in patch) {
    next.line_items = recalculateLineItemsForNights(next.line_items || [], next.nights);
  }
  return next;
}

function parseNum(value) {
  const n = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  return parseNum(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function computeTotals(draft, useFixedDiscount) {
  const lineTotal = (draft.line_items || []).reduce((sum, row) => sum + parseNum(row.amount), 0);
  let discount = 0;
  if (useFixedDiscount) {
    discount = parseNum(draft.discount_amount);
  } else {
    discount = (lineTotal * parseNum(draft.discount_percent)) / 100;
  }
  discount = Math.min(discount, lineTotal);
  const subtotal = Math.max(0, lineTotal - discount);
  const gst = draft.gst_included ? (subtotal * parseNum(draft.gst_percent)) / 100 : 0;
  const grandTotal = subtotal + gst;
  const paid = parseNum(draft.paid_amount);
  const remaining = Math.max(0, grandTotal - paid);
  return { lineTotal, discount, subtotal, gst, grandTotal, paid, remaining };
}

function fieldSx(isDark, drawerTheme, accentColor) {
  return {
    '& .MuiInputBase-root': {
      borderRadius: '10px',
      bgcolor: isDark ? alpha('#ffffff', 0.04) : '#ffffff',
      color: isDark ? drawerTheme.text : '#0f172a',
      fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
      color: drawerTheme.subtleText,
      fontSize: '0.8rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? alpha('#ffffff', 0.12) : alpha('#101828', 0.14),
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(accentColor, 0.45),
    },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: accentColor,
    },
  };
}

function buildDraftInvoicePayload(draft, invoiceScope, useFixedDiscount) {
  if (!draft) return null;
  return {
    invoice_scope: invoiceScope,
    line_items: (draft.line_items || []).map((row) => ({
      description: row.description || '',
      room_type: row.room_type || '',
      rate_plan: row.rate_plan || '',
      room_count: Math.max(1, parseInt(row.room_count, 10) || 1),
      amount: parseNum(row.amount),
      source: row.source || undefined,
    })),
    discount_percent: useFixedDiscount ? 0 : parseNum(draft.discount_percent),
    discount_amount: useFixedDiscount ? parseNum(draft.discount_amount) : null,
    gst_percent: parseNum(draft.gst_percent),
    gst_included: Boolean(draft.gst_included),
    paid_amount: draft.show_paid_remaining ? parseNum(draft.paid_amount) : null,
    show_paid_remaining: Boolean(draft.show_paid_remaining),
    guest_name: draft.guest_name || '',
    guest_address: draft.guest_address || '',
    invoice_number: draft.invoice_number || '',
    invoice_date: draft.invoice_date || '',
    check_in: draft.check_in || '',
    check_out: draft.check_out || '',
    check_in_time: formatTimeForApi(draft.check_in_time),
    check_out_time: formatTimeForApi(draft.check_out_time),
    nights: draft.nights != null ? parseInt(draft.nights, 10) : null,
  };
}

export default function BookingDraftInvoiceEditor({
  bookingId,
  invoiceScope = 'hotel',
  fetchDefaults = fetchDraftInvoiceDefaults,
  isDark,
  accentColor = '#4ce1b6',
  isMobile = false,
  onDraftChange,
  onGenerateDraft,
  onClearDraftCache,
  generating = false,
}) {
  const drawerTheme = getBookingDrawerTheme(isDark);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [defaultsError, setDefaultsError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [useFixedDiscount, setUseFixedDiscount] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const loadDefaults = useCallback(async () => {
    if (!bookingId) return;
    setLoadingDefaults(true);
    setDefaultsError(null);
    onClearDraftCache?.();
    try {
      const data = await fetchDefaults(bookingId, invoiceScope);
      const nights = effectiveNights(
        data.nights ?? computeNightsFromDates(data.check_in, data.check_out) ?? 1,
      );
      const nextDraft = {
        ...data,
        nights,
        line_items: recalculateLineItemsForNights(
          (data.line_items || []).map((row) => ({ ...row })),
          nights,
        ),
        invoice_scope: invoiceScope,
      };
      const nextUseFixedDiscount = data.discount_amount != null && parseNum(data.discount_amount) > 0;
      setUseFixedDiscount(nextUseFixedDiscount);
      setDraft(nextDraft);
      onDraftChange?.(nextDraft);
    } catch (err) {
      setDefaultsError(err?.response?.data?.error || err?.message || 'Failed to load draft defaults.');
    } finally {
      setLoadingDefaults(false);
    }
  }, [bookingId, invoiceScope, fetchDefaults, onClearDraftCache, onDraftChange]);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  useEffect(() => {
    if (!draft || !onGenerateDraft || loadingDefaults) return undefined;
    const payload = buildDraftInvoicePayload(draft, invoiceScope, useFixedDiscount);
    if (!payload) return undefined;
    const timer = window.setTimeout(() => {
      onGenerateDraft(payload);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [draft, invoiceScope, loadingDefaults, onGenerateDraft, useFixedDiscount]);

  const totals = useMemo(
    () => (draft ? computeTotals(draft, useFixedDiscount) : null),
    [draft, useFixedDiscount],
  );

  const updateDraft = (patch) => {
    setDraft((prev) => {
      let next = { ...prev, ...patch };
      if ('nights' in patch) {
        next = {
          ...next,
          line_items: recalculateLineItemsForNights(next.line_items || [], next.nights),
        };
      }
      onDraftChange?.(next);
      return next;
    });
  };

  const updateLineItem = (index, patch) => {
    setDraft((prev) => {
      const nights = effectiveNights(prev.nights);
      const lineItems = [...(prev.line_items || [])];
      let row = { ...lineItems[index], ...patch };

      if ('amount' in patch && !('rate_per_night' in patch)) {
        row.rate_per_night = computeRatePerNight(row.amount, row.room_count, nights);
      } else if ('rate_per_night' in patch || 'room_count' in patch) {
        row = normalizeLineItem(row, nights);
      } else if ('auto_calculate' in patch) {
        row = normalizeLineItem(row, nights);
      }

      lineItems[index] = row;
      const next = { ...prev, line_items: lineItems };
      onDraftChange?.(next);
      return next;
    });
  };

  const updateStayDates = (patch) => {
    setDraft((prev) => {
      const next = applyStayDatePatch(prev, patch);
      onDraftChange?.(next);
      return next;
    });
  };

  const addLineItem = () => {
    setDraft((prev) => {
      const next = { ...prev, line_items: [...(prev.line_items || []), emptyLineItem()] };
      onDraftChange?.(next);
      return next;
    });
  };

  const removeLineItem = (index) => {
    setDraft((prev) => {
      const lineItems = [...(prev.line_items || [])];
      lineItems.splice(index, 1);
      const next = { ...prev, line_items: lineItems.length ? lineItems : [emptyLineItem()] };
      onDraftChange?.(next);
      return next;
    });
  };

  const buildPayload = () => buildDraftInvoicePayload(draft, invoiceScope, useFixedDiscount);

  const handleGenerate = () => {
    const payload = buildPayload();
    if (payload) onGenerateDraft?.(payload, { force: true });
  };

  const inputSx = fieldSx(isDark, drawerTheme, accentColor);
  const datePickerSx = {
    ...inputSx,
    '& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? alpha('#ffffff', 0.12) : alpha('#101828', 0.14),
    },
    '& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(accentColor, 0.45),
    },
  };

  if (loadingDefaults && !draft) {
    return (
      <Box sx={{ py: 3, px: 1, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: drawerTheme.subtleText }}>
          Loading draft editor…
        </Typography>
      </Box>
    );
  }

  if (defaultsError && !draft) {
    return (
      <Stack spacing={1.5} sx={{ py: 1 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {defaultsError}
        </Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadDefaults}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (!draft) return null;

  const currency = draft.currency || 'INR';

  return (
    <Stack spacing={isMobile ? 1.5 : 2}>
      <Alert
        severity="info"
        sx={{
          borderRadius: 2,
          py: isMobile ? 0.75 : 1,
          bgcolor: isDark ? alpha(accentColor, 0.1) : alpha(accentColor, 0.08),
          color: isDark ? drawerTheme.text : '#0f172a',
          '& .MuiAlert-icon': { color: accentColor },
          '& .MuiAlert-message': { fontSize: { xs: '0.78rem', sm: '0.875rem' }, lineHeight: 1.45 },
        }}
      >
        Customize line items, GST, and discount for customer bills. Changes are preview-only and do not
        update booking or payment records.
      </Alert>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a' }}>
          Line items
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}>
          <Tooltip title="Reset to booking values">
            <IconButton size="small" onClick={loadDefaults} disabled={loadingDefaults}>
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addLineItem}
            sx={{ textTransform: 'none', fontWeight: 700, color: accentColor }}
          >
            Add room
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={1.25}>
        {(draft.line_items || []).map((row, index) => (
          <Box
            key={`draft-line-${index}`}
            sx={{
              p: 1.25,
              borderRadius: '12px',
              border: `1px solid ${alpha(accentColor, isDark ? 0.2 : 0.18)}`,
              bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#ffffff', 0.9),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: drawerTheme.subtleText }}>
                Item {index + 1}
              </Typography>
              {(draft.line_items || []).length > 1 ? (
                <IconButton size="small" onClick={() => removeLineItem(index)} aria-label="Remove line item">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Custom description (optional)"
                placeholder="e.g. AC Deluxe – Extra night charge"
                value={row.description || ''}
                onChange={(e) => updateLineItem(index, { description: e.target.value })}
                sx={inputSx}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="Room type"
                  value={row.room_type || ''}
                  onChange={(e) => updateLineItem(index, { room_type: e.target.value })}
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Rate plan"
                  value={row.rate_plan || ''}
                  onChange={(e) => updateLineItem(index, { rate_plan: e.target.value })}
                  sx={inputSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  label="Rooms"
                  value={row.room_count ?? 1}
                  onChange={(e) => updateLineItem(index, { room_count: e.target.value })}
                  inputProps={{ min: 1 }}
                  sx={{ ...inputSx, flex: { md: 1 } }}
                />
                {row.auto_calculate !== false ? (
                  <TextField
                    size="small"
                    type="number"
                    label={isMobile ? `Rate/night (${currency})` : `Rate per night (${currency})`}
                    value={row.rate_per_night ?? 0}
                    onChange={(e) => updateLineItem(index, { rate_per_night: e.target.value })}
                    inputProps={{ min: 0, step: '0.01' }}
                    sx={{ ...inputSx, flex: { md: 2 } }}
                  />
                ) : null}
                <TextField
                  size="small"
                  type="number"
                  label={`Total (${currency})`}
                  value={row.amount ?? 0}
                  onChange={(e) => updateLineItem(index, { amount: e.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  helperText={
                    row.auto_calculate !== false
                      ? `${effectiveNights(draft.nights)} night(s) × ${effectiveRooms(row.room_count)} room(s)`
                      : 'Fixed charge (not multiplied by nights)'
                  }
                  InputProps={{ readOnly: row.auto_calculate !== false }}
                  sx={{ ...inputSx, flex: { md: 2 } }}
                />
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={row.auto_calculate !== false}
                    onChange={(e) => updateLineItem(index, { auto_calculate: e.target.checked })}
                    sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
                  />
                }
                label={
                  <Typography variant="caption">
                    Multiply rate by nights ({effectiveNights(draft.nights)} night
                    {effectiveNights(draft.nights) !== 1 ? 's' : ''})
                  </Typography>
                }
              />
            </Stack>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ borderColor: alpha(accentColor, 0.16) }} />

      {draft.hide_stay_dates_section ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a' }}>
            Invoice details
          </Typography>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, display: 'block', mt: -0.75 }}>
            Shown on the invoice header. Changes are preview-only and do not update the order.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Invoice number"
                value={draft.invoice_number || ''}
                onChange={(e) => updateDraft({ invoice_number: e.target.value })}
                placeholder="Leave blank for auto-generated number"
                sx={inputSx}
              />
              <DatePicker
                label="Invoice date"
                format={getHotelDateFormat()}
                value={parseDisplayDate(draft.invoice_date)}
                onChange={(value) => updateDraft({ invoice_date: formatDisplayDate(value) })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: datePickerSx,
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>
        </>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a' }}>
            Stay dates
          </Typography>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, display: 'block', mt: -0.75 }}>
            Shown on the invoice under hotel details. Changing dates does not update the booking.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <DatePicker
                label="Check-in"
                format={getHotelDateFormat()}
                value={parseDisplayDate(draft.check_in)}
                onChange={(value) => updateStayDates({ check_in: formatDisplayDate(value) })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: datePickerSx,
                  },
                }}
              />
              <DatePicker
                label="Check-out"
                format={getHotelDateFormat()}
                value={parseDisplayDate(draft.check_out)}
                minDate={parseDisplayDate(draft.check_in) || undefined}
                onChange={(value) => updateStayDates({ check_out: formatDisplayDate(value) })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: datePickerSx,
                  },
                }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TimePicker
                label="Check-in time"
                value={parseDisplayTime(draft.check_in_time)}
                onChange={(value) => updateStayDates({ check_in_time: value?.isValid() ? value.format('HH:mm') : '' })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: datePickerSx,
                  },
                }}
              />
              <TimePicker
                label="Check-out time"
                value={parseDisplayTime(draft.check_out_time)}
                onChange={(value) => updateStayDates({ check_out_time: value?.isValid() ? value.format('HH:mm') : '' })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: datePickerSx,
                  },
                }}
              />
            </Stack>
            <TextField
              size="small"
              type="number"
              fullWidth
              label="Nights"
              value={draft.nights ?? 0}
              onChange={(e) => updateStayDates({ nights: e.target.value })}
              inputProps={{ min: 1 }}
              helperText="Auto-calculated from dates; updates room totals"
              sx={inputSx}
            />
          </LocalizationProvider>
        </>
      )}

      <Divider sx={{ borderColor: alpha(accentColor, 0.16) }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a' }}>
        Discount & GST
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={useFixedDiscount}
              onChange={(e) => setUseFixedDiscount(e.target.checked)}
              sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
            />
          }
          label={<Typography variant="body2">Use fixed discount amount</Typography>}
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        {useFixedDiscount ? (
          <TextField
            size="small"
            type="number"
            label="Discount amount"
            value={draft.discount_amount ?? 0}
            onChange={(e) => updateDraft({ discount_amount: e.target.value })}
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ ...inputSx, flex: 1 }}
          />
        ) : (
          <TextField
            size="small"
            type="number"
            label="Discount %"
            value={draft.discount_percent ?? 0}
            onChange={(e) => updateDraft({ discount_percent: e.target.value })}
            inputProps={{ min: 0, max: 100, step: '0.01' }}
            sx={{ ...inputSx, flex: 1 }}
          />
        )}
        <TextField
          size="small"
          type="number"
          label="GST %"
          value={draft.gst_percent ?? 0}
          onChange={(e) => updateDraft({ gst_percent: e.target.value })}
          inputProps={{ min: 0, max: 100, step: '0.01' }}
          sx={{ ...inputSx, flex: 1 }}
        />
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(draft.gst_included)}
            onChange={(e) => updateDraft({ gst_included: e.target.checked })}
            sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
          />
        }
        label={<Typography variant="body2">Apply GST on amount after discount</Typography>}
      />

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(draft.show_paid_remaining)}
            onChange={(e) => updateDraft({ show_paid_remaining: e.target.checked })}
            sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
          />
        }
        label={<Typography variant="body2">Show paid & remaining amounts on invoice</Typography>}
      />
      {draft.show_paid_remaining ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            size="small"
            type="number"
            label="Paid amount"
            value={draft.paid_amount ?? 0}
            onChange={(e) => updateDraft({ paid_amount: e.target.value })}
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ ...inputSx, flex: 1 }}
          />
          <TextField
            size="small"
            label="Remaining (calculated)"
            value={formatMoney(totals?.remaining)}
            InputProps={{ readOnly: true }}
            sx={{ ...inputSx, flex: 1 }}
          />
        </Stack>
      ) : null}

      <Button
        size="small"
        onClick={() => setShowAdvanced((v) => !v)}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, color: accentColor }}
      >
        {showAdvanced ? 'Hide' : 'Show'} guest overrides
      </Button>
      <Collapse in={showAdvanced}>
        <Stack spacing={1.25} sx={{ pt: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            label="Guest name"
            value={draft.guest_name || ''}
            onChange={(e) => updateDraft({ guest_name: e.target.value })}
            sx={inputSx}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Guest address"
            value={draft.guest_address || ''}
            onChange={(e) => updateDraft({ guest_address: e.target.value })}
            sx={inputSx}
          />
          {!draft.hide_stay_dates_section ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Invoice number"
                value={draft.invoice_number || ''}
                onChange={(e) => updateDraft({ invoice_number: e.target.value })}
                placeholder="Leave blank for DRAFT-{original}"
                sx={inputSx}
              />
              <TextField
                size="small"
                fullWidth
                label="Invoice date"
                value={draft.invoice_date || ''}
                onChange={(e) => updateDraft({ invoice_date: e.target.value })}
                placeholder="e.g. 21 Jun 2026"
                sx={inputSx}
              />
            </Stack>
          ) : null}
        </Stack>
      </Collapse>

      {totals ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: '12px',
            border: `1px solid ${alpha(accentColor, 0.24)}`,
            bgcolor: isDark ? alpha(accentColor, 0.08) : alpha(accentColor, 0.06),
          }}
        >
          <Stack spacing={0.35}>
            <Typography variant="caption" sx={{ color: drawerTheme.subtleText }}>
              Line items: {formatMoney(totals.lineTotal)} {draft.currency || 'INR'}
              {totals.discount > 0 ? ` · Discount: -${formatMoney(totals.discount)}` : ''}
              {totals.gst > 0 ? ` · GST: ${formatMoney(totals.gst)}` : ''}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a' }}>
              Grand total: {formatMoney(totals.grandTotal)} {draft.currency || 'INR'}
            </Typography>
          </Stack>
        </Box>
      ) : null}

      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={handleGenerate}
        disabled={generating || loadingDefaults}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 700,
          color: accentColor,
          borderColor: alpha(accentColor, 0.45),
          '&:hover': { borderColor: accentColor, bgcolor: alpha(accentColor, 0.08) },
        }}
      >
        {generating ? 'Updating preview…' : 'Refresh preview'}
      </Button>
    </Stack>
  );
}